import { FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { errorMessage } from '@/api/errorMessage';
import { useNotify } from '@/components/feedback/notificationsContext';
import { QueryState } from '@/components/QueryState';
import { SectionCard } from '@/components/SectionCard';
import { useAccess } from '@/session/access';
import { clientKeys, clientsApi } from './api';

const SCOPES = [
  { field: 'shareNutrition', label: 'Nutrição (refeições e metas)', module: 'nutrition' },
  { field: 'shareHealth', label: 'Saúde (peso, medidas, pressão)', module: 'health' },
  { field: 'shareFinance', label: 'Finanças', module: 'finance' },
] as const;

/**
 * LGPD consent, owned by the client: what each professional may see. Training
 * data is shared by nature of the service; personal logs are private unless
 * the client turns sharing on here, and it can be revoked at any time.
 */
export function SharingCard() {
  const can = useAccess();
  const queryClient = useQueryClient();
  const notify = useNotify();
  const professionals = useQuery({
    queryKey: clientKeys.myProfessionals,
    queryFn: clientsApi.myProfessionals,
  });
  const update = useMutation({
    mutationFn: ({ linkId, field, value }: { linkId: string; field: string; value: boolean }) =>
      clientsApi.setSharing(linkId, { [field]: value }),
    onSuccess: (data, { value }) => {
      queryClient.setQueryData(clientKeys.myProfessionals, data);
      notify(value ? 'Compartilhamento ativado.' : 'Compartilhamento desativado.', 'success');
    },
    // The switch stays where it was; say so, since this is a privacy choice.
    onError: (error) =>
      notify(
        errorMessage(error, 'Não foi possível alterar o compartilhamento. Nada foi mudado.'),
        'error',
      ),
  });
  const scopes = SCOPES.filter((scope) => can({ module: scope.module }));

  return (
    <QueryState query={professionals}>
      {(list) =>
        list.length === 0 || scopes.length === 0 ? null : (
          <SectionCard
            title="Quem acompanha você"
            description="Seus registros pessoais são privados. Escolha o que cada profissional pode ver; você pode mudar a qualquer momento."
          >
            <Stack spacing={2.5}>
              {list.map((pro) => (
                <div key={pro.linkId}>
                  <Typography variant="subtitle2">{pro.name ?? pro.email}</Typography>
                  <Stack>
                    {scopes.map((scope) => (
                      <FormControlLabel
                        key={scope.field}
                        control={
                          <Switch
                            checked={pro[scope.field]}
                            disabled={update.isPending}
                            onChange={(_, value) =>
                              update.mutate({ linkId: pro.linkId, field: scope.field, value })
                            }
                          />
                        }
                        label={scope.label}
                      />
                    ))}
                  </Stack>
                </div>
              ))}
            </Stack>
          </SectionCard>
        )
      }
    </QueryState>
  );
}
